import { useToast } from "@/components/podkit/toasts/use-toast";
import { type PlainEnvironment, useStartEnvironment, useStopEnvironment } from "@/queries/environment-queries";
import { type EffectiveState, environmentHasReachedStablePhase, showStart } from "@/routes/environments/phase";
import { formatError, MaximumEnvironmentLifetimeExceededError } from "@/utils/errors";
import { type FC, useCallback, useMemo, useState } from "react";
import { cn } from "@/components/podkit/lib/cn";
import { EnvironmentPhase } from "gitpod-next-api/gitpod/v1/environment_pb";
import { Heading1 } from "@/components/podkit/typography/Headings";
import { statusText } from "@/utils/environment";
import { Switch } from "@/components/Switch";
import { EnvironmentLifetimeExceededModal } from "@/components/environments/EnvironmentLifetimeExceededModal";

type State = "running" | "starting" | "stopped" | "stopping" | "deleting" | "deleted";

function stateFromEnvironment(environment: PlainEnvironment): State {
    const hasReachedStablePhase = environmentHasReachedStablePhase(environment);
    if (environment.spec?.desiredPhase === EnvironmentPhase.DELETED) {
        return hasReachedStablePhase ? "deleted" : "deleting";
    }
    if (showStart(environment)) {
        return hasReachedStablePhase ? "stopped" : "stopping";
    }

    return hasReachedStablePhase ? "running" : "starting";
}

export const EnvironmentSwitch: FC<{ environment: PlainEnvironment; effectiveState?: EffectiveState }> = ({
    environment,
    effectiveState,
}) => {
    const { toast } = useToast();
    const startEnvironment = useStartEnvironment();
    const stopEnvironment = useStopEnvironment();
    const isPendingAction = startEnvironment.isPending || stopEnvironment.isPending;

    const [showLifetimeExceededModal, setShowLifetimeExceededModal] = useState(false);

    const state = useMemo(() => stateFromEnvironment(environment), [environment]);

    const isTransitory = isPendingAction || state === "starting" || state === "stopping" || state === "deleting";
    const disabled = isTransitory || state === "deleted";
    const checked = state === "running" || state === "starting";

    const handleClick = useCallback(async () => {
        if (state === "stopped") {
            try {
                await startEnvironment.mutateAsync({ environmentId: environment.id });
            } catch (error) {
                if (error instanceof MaximumEnvironmentLifetimeExceededError) {
                    setShowLifetimeExceededModal(true);
                    return;
                }
                toast({
                    title: "Failed to start environment",
                    description: formatError(error),
                });
            }
        } else if (state === "running") {
            try {
                await stopEnvironment.mutateAsync(environment.id);
            } catch (error) {
                toast({
                    title: "Failed to stop environment",
                    description: formatError(error),
                });
            }
        }
    }, [environment.id, startEnvironment, state, stopEnvironment, toast]);

    return (
        <>
            <div className="inline-flex h-12 flex-wrap items-center justify-start gap-2">
                <Switch
                    state={isTransitory ? "pending" : checked ? "checked" : "unchecked"}
                    disabled={disabled}
                    onToggle={handleClick}
                    id={"toggle-env-" + environment.id}
                />
                <label
                    className={cn(
                        "grow cursor-pointer select-none justify-start text-base font-normal leading-tight text-content-primary",
                        { "cursor-default": disabled },
                    )}
                    htmlFor={"toggle-env-" + environment.id}
                    translate="no"
                >
                    <StatusText state={effectiveState} />
                </label>
            </div>
            <EnvironmentLifetimeExceededModal
                environment={environment}
                open={showLifetimeExceededModal}
                onClose={() => setShowLifetimeExceededModal(false)}
            />
        </>
    );
};

const StatusText: FC<{ state?: EffectiveState }> = ({ state }) => {
    const text = statusText(state);
    if (!text) {
        return null;
    }
    return <Heading1 className="text-wrap text-xl">{text}</Heading1>;
};
